import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Indicadores, RecorteDaVisaoGeral } from '../api/visao-geral.js';
import { consultarVisaoGeral } from '../api/visao-geral.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import { Botao } from '../componentes/Botao.js';
import { Cartao } from '../componentes/Cartao.js';
import { CartaoIndicador } from '../componentes/CartaoIndicador.js';
import type { Indicador } from '../componentes/CartaoIndicador.js';
import { formatarDataHora } from '../componentes/datas.js';
import { FiltroSelecao } from '../componentes/Filtros.js';
import type { OpcaoFiltro } from '../componentes/Filtros.js';
import {
  IconeAtencao,
  IconeAtualizar,
  IconeDispositivos,
  IconeFila,
  IconeOperacoes,
} from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { AtividadeRecente } from './visao-geral/AtividadeRecente.js';
import { CartaoRequerAtencao } from './visao-geral/CartaoRequerAtencao.js';
import { GraficoOperacoes } from './visao-geral/GraficoOperacoes.js';
import { plural } from './visao-geral/texto.js';
import { inicioDoPeriodo } from './movimentacoes/formato.js';
import './VisaoGeral.css';

/**
 * Janelas oferecidas, e por que são só duas.
 *
 * O gráfico é UMA BARRA POR HORA: 24 colunas já é o limite do que se lê num
 * cartão, e o contrato aceita até 31 dias (744 barras que ninguém enxerga).
 * Recorte largo é relatório, e relatório é a tela de Relatórios (F3.5).
 *
 * Os valores são os de `inicioDoPeriodo` — "1" é a janela móvel de 24h — para
 * o painel inteiro calcular período de um jeito só.
 */
const JANELAS: readonly OpcaoFiltro[] = [
  { valor: 'hoje', rotulo: 'Janela · hoje' },
  { valor: '1', rotulo: 'Últimas 24 horas' },
];

const JANELA_PADRAO = 'hoje';

/**
 * Ícone de cada indicador, pelo rótulo. É decoração: o rótulo continua dizendo
 * o que o número é, e um indicador sem entrada aqui simplesmente sai sem ícone.
 */
const ICONES_DOS_INDICADORES: Readonly<Record<string, ReactNode>> = {
  'Operações registradas': <IconeOperacoes tamanho={18} />,
  'Aguardando integração': <IconeFila tamanho={18} />,
  'Aparelhos com operador': <IconeDispositivos tamanho={18} />,
  'Divergências em aberto': <IconeAtencao tamanho={18} />,
};

/**
 * Os quatro números do topo — todos com coluna de origem no banco.
 *
 * Cada `nota` é a quebra do PRÓPRIO número, e não uma comparação com ontem: o
 * relógio das operações é o do APARELHO (RF-052), então uma operação de ontem
 * que chega hoje mudaria o número de ontem retroativamente. Um indicador que
 * anda sozinho para trás é pior do que indicador nenhum; a comparação só volta
 * quando existir fechamento de dia gravado, com data de apuração própria.
 */
export function indicadoresDoDia(indicadores: Indicadores): readonly Indicador[] {
  const { operacoes, filaDeIntegracao: fila, dispositivos } = indicadores;

  return [
    {
      rotulo: 'Operações registradas',
      valor: String(operacoes.total),
      tom: 'neutro',
      nota: `${String(operacoes.coleta)} coleta · ${String(operacoes.movimentacao)} movimentação · ${String(operacoes.contagemInventario)} inventário`,
      tomNota: 'neutro',
    },
    {
      // Backlog, e não recorte da janela: o que espera gente hoje espera hoje,
      // tenha chegado nesta hora ou anteontem.
      rotulo: 'Aguardando integração',
      valor: String(fila.registrada + fila.enviando),
      tom: fila.retida > 0 ? 'atencao' : 'neutro',
      nota: `${String(fila.retida)} esperando decisão no painel`,
      tomNota: fila.retida > 0 ? 'atencao' : 'neutro',
    },
    {
      rotulo: 'Aparelhos com operador',
      valor: String(dispositivos.comOperadorConectado),
      sufixo: `/ ${String(dispositivos.total)}`,
      tom: 'neutro',
      nota: `${plural(dispositivos.bloqueados, 'bloqueado', 'bloqueados')} · ${String(dispositivos.aguardandoPareamento)} aguardando pareamento`,
      tomNota: dispositivos.bloqueados > 0 ? 'atencao' : 'neutro',
    },
    {
      rotulo: 'Divergências em aberto',
      valor: String(indicadores.divergenciasEmAberto),
      tom: indicadores.divergenciasEmAberto > 0 ? 'erro' : 'neutro',
      nota: plural(indicadores.inventariosEmConflito, 'ciclo em conflito', 'ciclos em conflito'),
      tomNota: indicadores.inventariosEmConflito > 0 ? 'erro' : 'neutro',
    },
  ];
}

/**
 * Visão geral do dia (RF-070).
 *
 * Responde em cinco segundos se a operação está funcionando agora: números da
 * janela, ritmo por hora, o que acabou de chegar na trilha e o que exige
 * alguém. Uma tela, UMA requisição — quatro chamadas dariam quatro instantes
 * de apuração diferentes numa tela cuja pergunta é "como está agora".
 *
 * O que saiu junto com `dados-exemplo`, e o que faria cada um voltar:
 *
 * - **Selo "ERP conectado · 10:41"**: não existe conector Senior (Fila B é
 *   F2.3) nem coluna que registre contato com o ERP. Volta quando houver um
 *   `ultimaIntegracaoEm` de verdade para exibir.
 * - **"12 vs. ontem" e as setas de variação**: ver `indicadoresDoDia`.
 * - **Selo pulsante "em tempo real"**: não há SSE, WebSocket nem polling —
 *   `useConsulta` busca uma vez. Virou "Atualizado em …" com botão de
 *   atualizar, que é o que a tela realmente faz.
 * - **Link "Ver tudo" para /coletas**: a atividade agora é a trilha de
 *   auditoria, e o destino honesto é /auditoria, que ainda é `EmBreve` (F3.5).
 */
export function VisaoGeral() {
  const [janela, definirJanela] = useState(JANELA_PADRAO);

  const recorte = useMemo<RecorteDaVisaoGeral>(
    // `ate` fica com o padrão do servidor (o instante da apuração): mandar o
    // relógio do navegador como fim da janela cortaria as operações que
    // chegaram enquanto a página estava aberta.
    () => ({ ...seInformado('de', inicioDoPeriodo(janela)) }),
    [janela],
  );

  const buscar = useCallback(() => consultarVisaoGeral(recorte), [recorte]);
  const consulta = useConsulta(buscar);

  return (
    <Pagina
      contexto={
        consulta.dados === null
          ? 'Painel do dia'
          : `Atualizado em ${formatarDataHora(consulta.dados.geradoEm)}`
      }
      titulo="Visão geral"
      acoes={
        <>
          <FiltroSelecao rotulo="Janela" valor={janela} opcoes={JANELAS} aoMudar={definirJanela} />
          <Botao altura="media" onClick={consulta.recarregar}>
            <IconeAtualizar tamanho={15} />
            Atualizar
          </Botao>
        </>
      }
    >
      <Resultado
        consulta={consulta}
        espera="Carregando a visão geral…"
        // Armazém parado não é tela vazia: os números são zero, e cada bloco
        // afirma o próprio vazio no lugar certo.
        estaVazio={() => false}
        vazio={null}
      >
        {(visao) => (
          <>
            <div className="visao-geral__indicadores">
              {indicadoresDoDia(visao.indicadores).map((indicador) => (
                <CartaoIndicador
                  key={indicador.rotulo}
                  indicador={indicador}
                  icone={ICONES_DOS_INDICADORES[indicador.rotulo]}
                />
              ))}
            </div>

            <div className="visao-geral__corpo">
              <div className="visao-geral__coluna">
                <Cartao titulo="Operações por hora">
                  {visao.operacoesPorHoraTruncado && (
                    <AvisoDaTela tom="atencao">
                      O gráfico atingiu o teto de leitura do middleware: as barras mostram menos
                      operações do que aconteceram na janela.
                    </AvisoDaTela>
                  )}
                  <GraficoOperacoes baldes={visao.operacoesPorHora} />
                </Cartao>

                <Cartao titulo="Atividade recente" semRespiro>
                  <AtividadeRecente linhas={visao.atividadeRecente} />
                </Cartao>
              </div>

              <CartaoRequerAtencao
                itens={visao.requerAtencao}
                truncado={visao.requerAtencaoTruncado}
                geradoEm={visao.geradoEm}
              />
            </div>
          </>
        )}
      </Resultado>
    </Pagina>
  );
}
